import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, PanResponder, View, Platform } from 'react-native';
import { Configuration } from './../../../Domain/DTOs/Configuration/ConfigurationDTO';
import { component } from '../../Decorators/Component';
import { CoreConfig } from './../../../CoreConfig';
import { useDirection } from '../../Hooks/UseDirection';
export const SliderView = component({
    provider: CoreConfig.provider,
    moduleName: CoreConfig.moduleName,
    componentName: Configuration.ComponentNames.slider,
    style(theme) {
        var _a, _b, _c;
        return {
            container: {
                height: 40,
                justifyContent: 'center',
            },
            track: {
                height: 4,
                borderRadius: 10,
                backgroundColor: ((_a = theme.colors) === null || _a === void 0 ? void 0 : _a.link_color) || '#DBE2EB'
            },
            progress: {
                height: 4,
                borderRadius: 10,
                backgroundColor: ((_b = theme.colors) === null || _b === void 0 ? void 0 : _b.secondary_color) || 'rgba(17, 85, 204, 1)'
            },
            thumb: {
                backgroundColor: ((_c = theme.colors) === null || _c === void 0 ? void 0 : _c.secondary_color) || 'rgba(17, 85, 204, 1)',
                position: 'absolute',
                width: 20,
                height: 20,
                borderRadius: 10,
                top: -8,
                left: 0,
                marginStart: -2
            }
        };
    }
})(({ style, containerStyle, trackColor, startValue = 0, endValue = 1, value = 0, onChange }) => {
    const [containerWidth, setContainerWidth] = useState(0);
    const containerWidthRef = useRef(0);
    const pressed = useRef(false);
    const panX = useRef(new Animated.Value(0)).current;
    const timeoutRef = useRef();
    const valuesRef = useRef({ startValue, endValue });
    const [dir] = useDirection();
    const panResponder = useMemo(() => {
        return PanResponder.create({
            onStartShouldSetPanResponder: () => true,
            onMoveShouldSetPanResponder: () => true,
            onPanResponderGrant: (e) => {
                panX.flattenOffset();
                let value = e.nativeEvent.locationX - 10;
                if (dir === 'rtl') {
                    value = containerWidthRef.current - value;
                }
                panX.setValue(value);
                pressed.current = true;
            },
            onPanResponderMove: (e) => {
                let value = e.nativeEvent.locationX - 10;
                if (value < 0) {
                    value = 0;
                }
                else if (value > containerWidthRef.current) {
                    value = containerWidthRef.current;
                }
                if (dir === 'rtl') {
                    value = containerWidthRef.current - value;
                }
                panX.setValue(value);
            },
            onPanResponderRelease: () => {
                let value = panX.__getValue();
                if (value < 0) {
                    value = 0;
                }
                if (value > containerWidthRef.current) {
                    value = containerWidthRef.current;
                }
                const percentage = value / containerWidthRef.current;
                const range = valuesRef.current.endValue - valuesRef.current.startValue;
                const increaseValue = range * percentage;
                const result = valuesRef.current.startValue + increaseValue;
                onChange === null || onChange === void 0 ? void 0 : onChange(result);
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = undefined;
                    pressed.current = false;
                }
                timeoutRef.current = setTimeout(() => {
                    pressed.current = false;
                    timeoutRef.current = undefined;
                }, 150);
            }
        });
    }, []);
    const handleLayout = useCallback((e) => {
        setContainerWidth(e.nativeEvent.layout.width);
        containerWidthRef.current = e.nativeEvent.layout.width;
    }, []);
    useEffect(() => {
        valuesRef.current = { startValue, endValue };
    }, [startValue, endValue]);
    useEffect(() => {
        if (pressed.current) {
            return;
        }
        panX.setValue(((value - startValue) / endValue - startValue) * containerWidth);
    }, [value, containerWidth, startValue, endValue]);
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = undefined;
            }
        };
    }, []);
    const translateX = panX.interpolate({
        inputRange: [0, containerWidth],
        outputRange: dir === 'rtl' && Platform.OS === 'web'
            ? [containerWidth, 0]
            : [0, Platform.OS !== 'web' && dir === 'rtl' ? -containerWidth : containerWidth],
    });
    return (_jsx(View, Object.assign({ renderToHardwareTextureAndroid: true, style: [style === null || style === void 0 ? void 0 : style.container, containerStyle] }, panResponder.panHandlers, { children: _jsxs(View, { style: [style === null || style === void 0 ? void 0 : style.track, !!trackColor && { backgroundColor: trackColor }], onLayout: handleLayout, children: [_jsx(Animated.View, { renderToHardwareTextureAndroid: true, style: [
                        style === null || style === void 0 ? void 0 : style.progress,
                        { width: panX.interpolate({ inputRange: [0, containerWidth], outputRange: ['0%', '100%'], extrapolate: 'clamp' }) }
                    ] }), _jsx(Animated.View, { renderToHardwareTextureAndroid: true, pointerEvents: 'none', style: [
                        style === null || style === void 0 ? void 0 : style.thumb,
                        { transform: [{ translateX }] }
                    ] })] }) })));
});
